import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, DraggableCarousel } from "../../../../components/ui";
import { faArrowRight, faStar } from "@fortawesome/free-solid-svg-icons";
import { GameDataList } from "../../../games/models/responses";
import { BLOB_IMAGE_URL } from "../../../../config/appConfig";
import { BlobContainerConsts } from "../../../../models/constants/blobContainerConsts";
import { useNavigate } from "react-router-dom";
import { useRef, useState } from "react";
import { gameLogin } from "../../../games/services/casino.service";
import { toastService as toast } from "../../../../components/ui/toast/toast.service";
import { useAppDispatch } from "../../../../store/hooks";
import { changeLoaderState } from "../../../../reducers/appStateReducer";
import { useLanguage } from "../../../../config/localization/LanguageProvider";
import userDeviceStore from "../../../../store/device.store";

type CategoryWithGames = {
    categoryId: string;
    categoryName: string;
    categoryHindiName: string;
    categoryImagePath: string;
    games: GameDataList[];
};

type GameItemProps = {
    categoryWithGames?: CategoryWithGames;
    games?: GameDataList[];
    handleFavouriteToggle: (gameId: string, isFavourite: boolean) => Promise<void>;
};

export function GameItem({
    categoryWithGames,
    games,
    handleFavouriteToggle,
}: GameItemProps) {

    const { language } = useLanguage();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const accessModeSource = userDeviceStore((state) => state.accessModeSource);

    const isGameClickExecuting = useRef(false);
    const [isGameClickDisabled, setIsGameClickDisabled] = useState(false);

    const gameList = categoryWithGames?.games ?? games ?? [];

    const handleLaunchGame = async (game: GameDataList) => {
        if (isGameClickExecuting.current) return;

        dispatch(changeLoaderState(true));
        isGameClickExecuting.current = true;
        setIsGameClickDisabled(true);

        try {
            const baseUri = window.location.origin;

            const gameUrl = game.gameURL.includes("?")
                ? `${game.gameURL}&home=${encodeURIComponent(baseUri)}&exit_url=${encodeURIComponent(baseUri)}`
                : `${game.gameURL}?home=${encodeURIComponent(baseUri)}&exit_url=${encodeURIComponent(baseUri)}`;

            const launchUrl = await gameLogin(
                gameUrl,
                game.providerName,
                game.code,
                {
                    gameId: game.id,
                    gameSupplierId: game.gameSupplierId,
                    gameSupplierName: game.supplierName,
                    searchParam: "",
                    setCasinoError: (message) => {
                        toast.error(message);
                    },
                },
                accessModeSource.sourceType,
            );

            if (launchUrl) {
                navigate("/gamelaunch", { state: { launchUrl, redirectTo: "/" } });
            }
        } finally {
            await new Promise((resolve) => setTimeout(resolve, 1300));
            isGameClickExecuting.current = false;
            setIsGameClickDisabled(false);
            dispatch(changeLoaderState(false));
        }
    };

    const getGameImageUrl = (
        webImagePath: string | null | undefined
    ): string => {
        let imageUrl = "/images/ImageNotFound.jpg";
        const trimmedPath = webImagePath?.trim();

        if (trimmedPath) {
            imageUrl =
                trimmedPath.includes("http://") ||
                    trimmedPath.includes("https://")
                    ? trimmedPath
                    : `${BLOB_IMAGE_URL}${BlobContainerConsts.GameBanner}/${trimmedPath}`;
        }

        return imageUrl.replace(/'/g, "%27");
    };

    return (
        <div className={`${categoryWithGames ? "text-white bg-dark-gradient rounded-2xl mt-6 p-4" : ""}`}>
            {categoryWithGames && (
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-1">
                        <img
                            src={`${BLOB_IMAGE_URL}${BlobContainerConsts.GameCategory}/${categoryWithGames.categoryImagePath}`}
                            alt={categoryWithGames.categoryName}
                            className="h-6 w-6 object-contain"
                        />
                        <span className="text-lg md:text-xl font-semibold">
                            {language === "hi"
                                ? categoryWithGames.categoryHindiName
                                : categoryWithGames.categoryName}
                        </span>
                    </div>

                    <Button
                        type="button"
                        className="flex h-8 w-8 items-center justify-center rounded-full bg-[#0d0f19] text-white shadow-md hover:scale-105 transition-transform"
                        onClick={() => {
                            navigate(`/casino`, {
                                state: {
                                    selectedCategoryId:
                                        categoryWithGames.categoryId,
                                },
                            });
                        }}
                    >
                        <FontAwesomeIcon icon={faArrowRight} className="text-sm" />
                    </Button>
                </div>
            )}

            <DraggableCarousel gap="gap-8">
                {gameList.map((game) => (
                    <div
                        key={game.id}
                        className={`group relative w-[110px] md:w-[170px] flex-shrink-0 ${isGameClickDisabled ? "cursor-default" : "cursor-pointer"
                            }`}
                        onClick={() => handleLaunchGame(game)}
                    >
                        <div className="relative rounded-xl overflow-hidden">
                            <img
                                src={getGameImageUrl(game.webImagePath)}
                                alt={game.name}
                                className="w-full aspect-[3/4] object-cover"
                            />

                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />

                            <Button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleFavouriteToggle(game.id, !game.isFavourite);
                                }}
                                className={`absolute top-2 right-2 p-1 transition-opacity ${game.isFavourite
                                    ? "opacity-100"
                                    : "opacity-0 group-hover:opacity-100"
                                    }`}
                            >
                                <FontAwesomeIcon
                                    icon={faStar}
                                    className={`w-4 h-4 ${game.isFavourite
                                        ? "text-[#FFD700]"
                                        : "text-transparent"
                                        }`}
                                    style={
                                        game.isFavourite
                                            ? {}
                                            : {
                                                stroke: "white",
                                                strokeWidth: 40,
                                            }
                                    }
                                />
                            </Button>
                        </div>

                        <span
                            className="block text-sm mt-2 truncate"
                            title={game.name}
                        >
                            {game.name}
                        </span>
                    </div>
                ))}
            </DraggableCarousel>
        </div>
    );
}